//LIBRARIES
import React from 'react';
import Auth from 'j-toker';
import styled from 'styled-components';
import { connect } from 'react-redux';
import moment from 'moment';

import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import { ExclamationCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { Button } from 'antd';

//COMPONENTS
import Select from 'components/ui/SelectField';
import DatePicker from 'components/ui/DatePicker';
import CheckboxNormal from 'components/ui/CheckboxNormal';
import Form from 'components/ui/Form';
import Spacer from 'components/ui/Spacer';
import Col from 'components/ui/Col';
import { RenderIfToggleOn } from 'components/shared/ToggleRenderer';
import Banner from 'components/ui/Banner';
import Row from 'components/ui/Row';

// Actions
import { acceptDeclineProposedSchedule } from 'actions/maintenanceActions';
import { getTicket } from 'actions/maintenanceTicketActions';

//HELPERS
import { media } from 'helpers/MediaHelper';
import { getTranslatedString, getCurrentLocale } from 'helpers/i18n';
import { featureEnabled } from 'helpers/ToggleBot';
import { iconColors, colors } from 'helpers/ColorHelper';
import { maintenanceTicketStatuses } from 'helpers/constants/Statuses';
import { maintenanceUserTypes } from 'helpers/constants/UserRoles';

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
`;

const StyledSection = styled.div`
  margin-bottom: 16px;
`;

const StyledRow = styled.div`
  flex-direction: row;
  display: flex;
  justify-content: space-between;
  ${media.mobile`
    flex-direction: column;
  `};
`;

const StyledItem = styled.div`
  width: ${(props) => props.width || '100%'};
  text-align: ${(props) => props.textAlign || 'left'};
  margin: ${(props) => props.margin || '0px'};
  margin-bottom: 12px;
  ${media.mobile`
    flex-direction: column;
    width: 100%;
  `};
`;

const StyledItemLabel = styled.div`
  margin-bottom: 5px;
`;

const SectionTitle = styled.span`
  font-size: 20px;
  font-family: 'Circular-Bold';
`;

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
`;

const StyledScheduledTimeWarningHeader = styled.p`
  font-size: 16px;
  line-height: 16px;
`;

const StyledButtonContainer = styled(Row)``;

const StyledButton = styled(Button)`
  background-color: ${(props) => (props.dark ? colors.DARK_BUTTON : colors.WHITE)};
  color: ${(props) => (props.dark ? colors.WHITE : colors.DARK_BUTTON)};
  float: right;
  margin-left: 8px;
  border: 1px solid ${colors.DARK_BUTTON};
  border-radius: 5px;
  padding: 4px 24px;
`;

const StyledP = styled.p`
  font-size: 14px;
  padding-top: 4px;
  padding-bottom: 16px;
`;

const StyledBannerWrapper = styled.div`
  padding-top: 40px;
  padding-bottom: 40px;
`;

const showScheduledNotice = (values, property_maintenance_dispatch) => {
  const userIsMaintenance = Auth.user.type === 'maintenance';

  if (!property_maintenance_dispatch) return false;

  if (userIsMaintenance) return false;

  if (values.status === maintenanceTicketStatuses.SCHEDULED && !values.proposed_scheduled)
    return false;

  return true;
};

const showScheduledTimeWarning = (
  values,
  initialValues,
  propertyIsMaintenanceDispatch,
  userIsMaintenance
) => {
  if (!propertyIsMaintenanceDispatch) return false;
  if (userIsMaintenance) return false;
  if (!featureEnabled('maintenance.maintenance_dispatch')) return false;
  if (!values.scheduled) return false;

  let startTime = values.scheduled_starts_at_time;
  let scheduledStartsAtDate = values.scheduled_starts_at_date;
  const initialStartTime = initialValues.scheduled_starts_at_time;
  const initialScheduledStartsAtDate = initialValues.scheduled_starts_at_date;

  if (startTime === initialStartTime && scheduledStartsAtDate === initialScheduledStartsAtDate)
    return false;

  if (!scheduledStartsAtDate) return false; // values.scheduled can default as undefined

  if (typeof scheduledStartsAtDate !== 'string') {
    // apparently values.scheduled_starts_at_date is sometimes a date object, and sometimes a string
    scheduledStartsAtDate = scheduledStartsAtDate.toISOString().substring(0, 10);
  }

  const scheduledDate = moment(`${scheduledStartsAtDate} ${startTime}`);

  const dateBuffer = moment().add(24, 'hours');

  if (scheduledDate.isAfter(dateBuffer)) return false;

  return true;
};

const ScheduledTimeWarning = () => (
  <Card>
    <CardContent style={{ display: 'flex', paddingBottom: '0px' }}>
      <ExclamationCircleOutlined style={{ color: iconColors.EXCLAMATION_CIRCLE_FILLED }} />
      <div style={{ marginLeft: '10px' }}>
        <StyledScheduledTimeWarningHeader>
          {getTranslatedString(
            'landlordPortal.maintenance.tickets.assignmentAndScheduling.LeadTime'
          )}
        </StyledScheduledTimeWarningHeader>
        <p>
          {getTranslatedString(
            'landlordPortal.maintenance.tickets.assignmentAndScheduling.LeadTimeBody'
          )}
        </p>
      </div>
    </CardContent>
  </Card>
);

const ProposedScheduleActions = ({ handleAcceptDecline }) => {
  return (
    <StyledButtonContainer>
      <Col span={24}>
        {getTranslatedString(
          'landlordPortal.maintenance.tickets.assignmentAndScheduling.status.proposedScheduledDate.bannerText'
        )}
      </Col>
      <Col span={24}>
        <StyledButton onClick={() => handleAcceptDecline('accept')} dark>
          {getTranslatedString('landlordPortal.editMaintenanceTicketForm.yes')}
        </StyledButton>
        <StyledButton onClick={() => handleAcceptDecline('decline')} type="ghost">
          {getTranslatedString('landlordPortal.editMaintenanceTicketForm.no')}
        </StyledButton>
      </Col>
    </StyledButtonContainer>
  );
};

const renderScheduledSuccessBanner = (assignedUser, values) => {
  if (
    !assignedUser ||
    assignedUser.maintenance_user_type !== maintenanceUserTypes.ZONE_LEAD || // user has not reassigned maintenance ticket after ticket set to scheduled
    !values.scheduled ||
    values.previous_status !== maintenanceTicketStatuses.PROPOSED_SCHEDULED_DATE // this is only for tickets moving from proposed_scheduled_date to scheduled
  ) {
    return null;
  }

  return (
    <StyledBannerWrapper>
      <Banner
        message={getTranslatedString(
          'landlordPortal.maintenance.tickets.assignmentAndScheduling.proposedScheduledSuccess.title'
        )}
        description={getTranslatedString(
          'landlordPortal.maintenance.tickets.assignmentAndScheduling.proposedScheduledSuccess.description'
        )}
        closable
        showIcon
        icon={<CheckCircleOutlined style={{ color: iconColors.CHECK_CIRCLE_OUTLINED }} />}
      />
    </StyledBannerWrapper>
  );
};

const AssignmentAndScheduling = ({
  values,
  errors,
  touched,
  initialValues,
  setFieldValue,
  maintenanceStaff,
  startTimeOptions,
  durationOptions,
  canAssignAndScheduleTickets,
  canAssignTicketsMaint,
  canLogTravelAndWorkTimes,
  mode,
  readOnly,
  acceptDeclineProposedSchedule,
  getTicket,
  setProposedScheduledResponse,
  setIsLoading,
  currentProperty,
  property_maintenance_dispatch_edit,
}) => {
  if (mode === 'create' && !canAssignAndScheduleTickets) return <React.Fragment />;

  const userIsMaintenance = Auth.user.type === 'maintenance';
  const showAcceptDeclineProposed =
    userIsMaintenance && values.status === maintenanceTicketStatuses.PROPOSED_SCHEDULED_DATE;
  const assignedUser = maintenanceStaff.find(
    (user) => values.assign && user.value === values.assign.value
  );

  const sectionTitle = featureEnabled('maintenance.scheduling')
    ? getTranslatedString(
        'landlordPortal.maintenance.tickets.assignmentAndScheduling.assignmentAndScheduling'
      )
    : getTranslatedString('landlordPortal.maintenance.tickets.assignmentAndScheduling.assignment');

  /**
   * property_maintenance_dispatch_edit is passed in when clicking on an existing ticket. The ticket list the property maintenance dispatch ticket details.
   * current property is used if you are creating a new ticket, or changing the property of an existing ticket. Current property details are retrieved via an api call when the property is changed
   * if editing a ticket and the property has not been changed, current property in the store will be empty, as it is reset every time the drawer opens
   */
  let propertyIsMaintenanceDispatch = !!currentProperty
    ? currentProperty.maintenance_dispatch === 'true'
    : property_maintenance_dispatch_edit;

  const handleAcceptDecline = (response) => {
    setIsLoading(true);
    acceptDeclineProposedSchedule(values.id, { response: response })
      .done(() => {
        getTicket(values.id).done(() => {
          setIsLoading(false);
          setProposedScheduledResponse(response);
        });
      })
      .catch(() => setIsLoading(false));
  };

  return (
    <React.Fragment>
      <StyledContainer>
        <StyledSection>
          <Spacer mb={0.5}>
            <SectionTitle>{sectionTitle}</SectionTitle>
          </Spacer>
          <StyledRow>
            <StyledItem>
              <StyledItemLabel>
                {getTranslatedString(
                  'landlordPortal.maintenance.tickets.assignmentAndScheduling.assignees'
                )}
              </StyledItemLabel>
              <AssigneeSelectField
                maintenanceStaff={maintenanceStaff}
                setFieldValue={setFieldValue}
                values={values}
                canAssignAndScheduleTickets={canAssignAndScheduleTickets || canAssignTicketsMaint}
                propertyMaintenanceDispatch={propertyIsMaintenanceDispatch}
              />
            </StyledItem>
          </StyledRow>
          <RenderIfToggleOn toggle="maintenance.scheduling">
            <StyledRow>
              {propertyIsMaintenanceDispatch && (
                <Col span={12}>
                  <StyledItem>
                    <CheckboxNormal
                      name="proposed_scheduled"
                      value={values.proposed_scheduled}
                      checked={values.proposed_scheduled}
                      onChange={(e) => {
                        setFieldValue('proposed_scheduled', e.target.checked);
                        setFieldValue('scheduled', e.target.checked);
                      }}
                      style={{ marginTop: '15px' }}
                      disabled={!canAssignAndScheduleTickets || readOnly}
                    >
                      {getTranslatedString(
                        'landlordPortal.editMaintenanceTicketForm.proposeScheduled'
                      )}
                    </CheckboxNormal>
                  </StyledItem>
                </Col>
              )}
              <Col span={propertyIsMaintenanceDispatch ? 12 : 24}>
                <StyledItem>
                  <CheckboxNormal
                    name="scheduled"
                    value={values.scheduled}
                    checked={values.scheduled && !values.proposed_scheduled}
                    onChange={(e) => {
                      setFieldValue('scheduled', e.target.checked);
                      setFieldValue('proposed_scheduled', false);
                    }}
                    style={{ marginTop: '15px' }}
                    disabled={!canAssignAndScheduleTickets || readOnly}
                  >
                    {getTranslatedString('landlordPortal.editMaintenanceTicketForm.scheduled')}
                  </CheckboxNormal>
                </StyledItem>
              </Col>
            </StyledRow>
          </RenderIfToggleOn>
          {values.scheduled && (
            <div>
              {showScheduledNotice(values, propertyIsMaintenanceDispatch) && (
                <StyledRow>
                  <StyledP>
                    {getTranslatedString(
                      `landlordPortal.maintenance.tickets.assignmentAndScheduling.${
                        values.proposed_scheduled ? 'proposedScheduledNotice' : 'scheduledNotice'
                      }`
                    )}
                  </StyledP>
                </StyledRow>
              )}
              <StyledRow>
                <StyledItem width="30%">
                  <StyledItemLabel>
                    {getTranslatedString(
                      'landlordPortal.maintenance.tickets.assignmentAndScheduling.startTime'
                    )}
                  </StyledItemLabel>
                  <Form.Item
                    validateStatus={
                      touched.scheduled_starts_at_time && errors.scheduled_starts_at_time
                        ? 'error'
                        : null
                    }
                    help={touched.scheduled_starts_at_time && errors.scheduled_starts_at_time}
                  >
                    <Select
                      name="scheduled_starts_at_time"
                      showSearch
                      placeholder={getTranslatedString(
                        'landlordPortal.maintenance.tickets.assignmentAndScheduling.select'
                      )}
                      size="large"
                      value={values.scheduled_starts_at_time}
                      onChange={(value) => {
                        setFieldValue('scheduled_starts_at_time', value);
                      }}
                      disabled={!canAssignAndScheduleTickets || readOnly}
                    >
                      {startTimeOptions.map((option) => (
                        <Select.Option value={option}>{option}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </StyledItem>
                <StyledItem width="30%">
                  <StyledItemLabel>
                    {getTranslatedString(
                      'landlordPortal.maintenance.tickets.assignmentAndScheduling.scheduledDate'
                    )}
                  </StyledItemLabel>
                  <Form.Item
                    validateStatus={
                      touched.scheduled_starts_at_date && errors.scheduled_starts_at_date
                        ? 'error'
                        : null
                    }
                    help={touched.scheduled_starts_at_date && errors.scheduled_starts_at_date}
                  >
                    <StyledDatePicker
                      name="scheduled_starts_at_date"
                      size="large"
                      value={values.scheduled_starts_at_date}
                      defaultPickerValue={moment().locale(getCurrentLocale())}
                      showToday={false}
                      placeholder="e.g. 2020-01-01"
                      mobileMode="date"
                      onChange={(value) => setFieldValue('scheduled_starts_at_date', value)}
                      disabled={!canAssignAndScheduleTickets || readOnly}
                    />
                  </Form.Item>
                </StyledItem>
                <StyledItem width="30%">
                  <StyledItemLabel>
                    {`${getTranslatedString(
                      'landlordPortal.maintenance.tickets.assignmentAndScheduling.duration'
                    )}`}
                  </StyledItemLabel>
                  <Form.Item>
                    <Select
                      name="scheduled_duration"
                      showSearch
                      placeholder={getTranslatedString(
                        'landlordPortal.maintenance.tickets.assignmentAndScheduling.select'
                      )}
                      size="large"
                      value={values.scheduled_duration}
                      onChange={(value) => {
                        setFieldValue('scheduled_duration', value);
                      }}
                      disabled={!canAssignAndScheduleTickets || readOnly}
                    >
                      {durationOptions.map((option) => (
                        <Select.Option value={option}>{option} h</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </StyledItem>
              </StyledRow>
            </div>
          )}
          {propertyIsMaintenanceDispatch &&
            userIsMaintenance &&
            renderScheduledSuccessBanner(assignedUser, values)}
          {showScheduledTimeWarning(
            values,
            initialValues,
            propertyIsMaintenanceDispatch,
            userIsMaintenance
          ) && <ScheduledTimeWarning />}
          <RenderIfToggleOn
            toggle="maintenance.maintenance_dispatch"
            dontRenderIf={!showAcceptDeclineProposed}
          >
            <Banner
              description={<ProposedScheduleActions handleAcceptDecline={handleAcceptDecline} />}
              showIcon
              icon={
                <ExclamationCircleOutlined
                  style={{ color: iconColors.EXCLAMATION_CIRCLE_FILLED }}
                />
              }
            />
          </RenderIfToggleOn>
        </StyledSection>

        <RenderIfToggleOn toggle="ui.maintenance.travel_time">
          <StyledSection>
            <Spacer mb={0.5}>
              <SectionTitle>
                {getTranslatedString(
                  'landlordPortal.maintenance.tickets.assignmentAndScheduling.travelTimeToSite'
                )}
              </SectionTitle>
            </Spacer>

            <StyledRow>
              <StyledItem width="48%">
                <StyledItemLabel>
                  {getTranslatedString(
                    'landlordPortal.maintenance.tickets.assignmentAndScheduling.hours'
                  )}
                </StyledItemLabel>
                <Select
                  name="travel_time_hour"
                  showSearch
                  placeholder={getTranslatedString(
                    'landlordPortal.maintenance.tickets.assignmentAndScheduling.select'
                  )}
                  size="large"
                  value={values.travel_time_hour}
                  onChange={(value) => {
                    setFieldValue('travel_time_hour', value);
                  }}
                  disabled={!canLogTravelAndWorkTimes || readOnly}
                >
                  {[...Array(24).keys()].map((option) => (
                    <Select.Option value={option}>{option}</Select.Option>
                  ))}
                </Select>
              </StyledItem>
              <StyledItem width="48%">
                <StyledItemLabel>
                  {getTranslatedString(
                    'landlordPortal.maintenance.tickets.assignmentAndScheduling.minutes'
                  )}
                </StyledItemLabel>
                <Select
                  name="travel_time_minute"
                  showSearch
                  placeholder={getTranslatedString(
                    'landlordPortal.maintenance.tickets.assignmentAndScheduling.select'
                  )}
                  size="large"
                  value={values.travel_time_minute}
                  onChange={(value) => {
                    setFieldValue('travel_time_minute', value);
                  }}
                  disabled={!canLogTravelAndWorkTimes || readOnly}
                >
                  {[0, 15, 30, 45].map((option) => (
                    <Select.Option value={option}>{option}</Select.Option>
                  ))}
                </Select>
              </StyledItem>
            </StyledRow>
          </StyledSection>
        </RenderIfToggleOn>
        <RenderIfToggleOn toggle="ui.maintenance.time_worked">
          <StyledSection>
            <Spacer mb={0.5}>
              <SectionTitle>
                {getTranslatedString(
                  'landlordPortal.maintenance.tickets.assignmentAndScheduling.timeWorked'
                )}
              </SectionTitle>
            </Spacer>

            <StyledRow>
              <StyledItem width="48%">
                <StyledItemLabel>
                  {getTranslatedString(
                    'landlordPortal.maintenance.tickets.assignmentAndScheduling.hours'
                  )}
                </StyledItemLabel>
                <Select
                  name="worked_time_hour"
                  showSearch
                  placeholder={getTranslatedString(
                    'landlordPortal.maintenance.tickets.assignmentAndScheduling.select'
                  )}
                  size="large"
                  value={values.worked_time_hour}
                  onChange={(value) => {
                    setFieldValue('worked_time_hour', value);
                  }}
                  disabled={!canLogTravelAndWorkTimes || readOnly}
                >
                  {[...Array(24).keys()].map((option) => (
                    <Select.Option value={option}>{option}</Select.Option>
                  ))}
                </Select>
              </StyledItem>
              <StyledItem width="48%">
                <StyledItemLabel>
                  {getTranslatedString(
                    'landlordPortal.maintenance.tickets.assignmentAndScheduling.minutes'
                  )}
                </StyledItemLabel>
                <Select
                  name="worked_time_minute"
                  showSearch
                  placeholder={getTranslatedString(
                    'landlordPortal.maintenance.tickets.assignmentAndScheduling.select'
                  )}
                  size="large"
                  value={values.worked_time_minute}
                  onChange={(value) => {
                    setFieldValue('worked_time_minute', value);
                  }}
                  disabled={!canLogTravelAndWorkTimes || readOnly}
                >
                  {[0, 15, 30, 45].map((option) => (
                    <Select.Option value={option}>{option}</Select.Option>
                  ))}
                </Select>
              </StyledItem>
            </StyledRow>
          </StyledSection>
        </RenderIfToggleOn>
      </StyledContainer>
    </React.Fragment>
  );
};

export default connect(
  (store) => {
    return {
      maintenanceStaff: store.maintenanceReducer.maintenance,
      startTimeOptions: store.companyDetails.companyPreferences.ticket_scheduling_starts_at_times,
      durationOptions: store.companyDetails.companyPreferences.ticket_scheduling_durations,
      canAssignAndScheduleTickets: store.userTypes.canAssignAndScheduleTickets,
      canAssignTicketsMaint: store.userTypes.canAssignTicketsMaint,
      canLogTravelAndWorkTimes: store.userTypes.canLogTravelAndWorkTimes,
      currentProperty: store.landlordProperties.property,
    };
  },
  { acceptDeclineProposedSchedule, getTicket }
)(AssignmentAndScheduling);

const AssigneeSelectField = ({
  maintenanceStaff,
  setFieldValue,
  values,
  canAssignAndScheduleTickets,
  readOnly,
  propertyMaintenanceDispatch,
}) => {
  const handleAssigneeChange = (val) => {
    const originalAssignee = values.assign;
    const [type, value, label] = val.split('$');
    setFieldValue('assign_string', val);
    setFieldValue('assign', { type, value, label });

    // Auto-assign new status to assigned when someone added or back to reviewed when removed.
    if (!featureEnabled('ui.create_service_request.assign_status')) return;
    const noAssignee = !value;
    const currentStatus = values.status;

    if (noAssignee && originalAssignee?.value && currentStatus === 'assigned') {
      setFieldValue('status', 'Reviewed');
    } else if (!noAssignee && originalAssignee?.value !== value) {
      setFieldValue('status', 'assigned');
    }
  };

  const groupings = maintenanceStaff
    .map((staff) => staff.type)
    .filter((currVal, index, arr) => arr.indexOf(currVal) === index);

  let groupedOptions = [
    {
      label: getTranslatedString('landlordPortal.maintenance.tickets.assignmentAndScheduling.none'),
      options: [
        {
          label: getTranslatedString(
            'landlordPortal.maintenance.tickets.assignmentAndScheduling.none'
          ),
          value: null,
          type: '',
        },
      ],
    },
  ];

  groupings.forEach((grouping) => {
    groupedOptions.push({
      label: grouping,
      options: maintenanceStaff.filter((option) => option.type === grouping),
    });
  });

  return (
    <Select
      name="assign_string"
      showSearch
      placeholder={getTranslatedString(
        'landlordPortal.maintenance.tickets.assignmentAndScheduling.select'
      )}
      size="large"
      value={
        values.assign_string ||
        getTranslatedString('landlordPortal.maintenance.tickets.assignmentAndScheduling.none')
      }
      onChange={handleAssigneeChange}
      disabled={!canAssignAndScheduleTickets || readOnly}
    >
      {groupedOptions.map((group, i) => (
        <Select.OptGroup label={group.label} key={i}>
          {group.options.map((option) => (
            <Select.Option
              value={`${option.pure_type}$${option.value || ''}$${option.label}`}
              key={`${option.type}-${option.value}`}
              disabled={disableAssignmentOption(
                option.maintenance_user_type,
                propertyMaintenanceDispatch
              )}
            >
              {option.label}
            </Select.Option>
          ))}
        </Select.OptGroup>
      ))}
    </Select>
  );
};

const disableAssignmentOption = (userType, propertyMaintenanceDispatch) => {
  // zone leads and maintenance users can assign any maintenance user
  if (Auth.user.type === 'maintenance') return false;

  return (
    featureEnabled('maintenance.maintenance_dispatch') &&
    propertyMaintenanceDispatch &&
    userType === 'maintenance'
  );
};
